import React, { useState, useEffect } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  AccordionProps,
} from '@mui/material';

interface ExpandableCardProps extends Omit<AccordionProps, 'children' | 'title'> {
  title: string | React.ReactNode;
  children: React.ReactNode;
  summary?: React.ReactNode; // Optional summary shown when collapsed
  defaultOpen?: boolean;
}

/**
 * ExpandableCard - A collapsible card component for content that can be expanded/collapsed
 * Can show a summary when collapsed and full content when expanded
 *
 * Usage:
 * - With summary: Pass summary prop for content shown when collapsed
 * - Legacy mode: Pass two children - first is summary, second is full content
 * - Simple mode: Pass single child for content only shown when expanded
 */
export const ExpandableCard: React.FC<ExpandableCardProps> = ({
  title,
  children,
  summary,
  defaultOpen = true,
  ...props
}) => {
  const [expanded, setExpanded] = useState(defaultOpen);

  // Handle legacy ContentCard pattern with two children
  const childArray = React.Children.toArray(children);
  const hasTwoChildren = childArray.length === 2 && !summary;

  const summaryContent = summary || (hasTwoChildren ? childArray[0] : null);
  const fullContent = hasTwoChildren ? childArray[1] : children;

  return (
    <Accordion expanded={expanded} onChange={(_, isExpanded) => setExpanded(isExpanded)} {...props}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel-content"
        id="panel-header"
        sx={{
          flexDirection: summaryContent && !expanded ? 'column' : 'row',
          alignItems: summaryContent && !expanded ? 'flex-start' : 'center',
        }}
      >
        <Box sx={{ width: '100%' }}>
          {typeof title === 'string' ? (
            <Typography variant="h6" component="h3">
              {title}
            </Typography>
          ) : (
            title
          )}
          {!expanded && summaryContent && <Box sx={{ mt: 1 }}>{summaryContent}</Box>}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2 }}>{fullContent}</AccordionDetails>
    </Accordion>
  );
};
